import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BrandMark } from './BrandMark';

const CONTACT_EMAIL = 'hello@cadencez.com';

/**
 * Editorial site footer. Three navigable groups (Product / Trust & Legal /
 * brand column) plus a contact mailto. Every link resolves to a real route
 * in App.tsx or a mailto: — no dead links.
 */
export function SiteFooter() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  const linkClass = 'text-foreground/55 hover:text-foreground transition-colors';

  const groups: { heading: string; links: { to: string; label: string }[] }[] = [
    {
      heading: t('footer.group_product', { defaultValue: 'Product' }),
      links: [
        { to: '/pricing', label: t('nav.pricing') },
        { to: '/compare', label: t('nav.compare') },
        { to: '/demo', label: t('nav.demo') },
        { to: '/login', label: t('nav.login') },
        { to: '/signup', label: t('nav.signup') },
      ],
    },
    {
      heading: t('footer.group_legal', { defaultValue: 'Trust & legal' }),
      links: [
        { to: '/security', label: t('footer.security') },
        { to: '/terms', label: t('footer.terms') },
        { to: '/privacy', label: t('footer.privacy') },
        { to: '/dpa', label: t('footer.dpa') },
      ],
    },
  ];

  return (
    <footer className="border-t border-border mt-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 gap-8 sm:grid-cols-4 text-sm">
        {/* Brand column */}
        <div className="col-span-2 sm:col-span-2 space-y-4">
          <BrandMark />
          <p className="max-w-xs text-foreground/55 leading-relaxed">{t('footer.tag')}</p>
          <a href={`mailto:${CONTACT_EMAIL}`} className={`inline-block ${linkClass}`}>
            {CONTACT_EMAIL}
          </a>
        </div>

        {/* Navigable groups */}
        {groups.map((group) => (
          <nav key={group.heading} className="space-y-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-foreground/40">
              {group.heading}
            </div>
            <ul className="space-y-2.5">
              {group.links.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className={linkClass}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </div>

      <div className="border-t border-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-5 text-xs text-foreground/40">
          {t('footer.copyright', {
            year,
            defaultValue: '© {{year}} CadencEZ Inc. All rights reserved.',
          })}
        </div>
      </div>
    </footer>
  );
}
